// Каркас CRM: меню разделов, шапка с поиском, свежесть данных.
//
// СОСТАВ МЕНЮ ЗДЕСЬ НЕ ЖИВЁТ — он в components/nav.ts. Новый раздел = одна
// строка там. Здесь только поведение: сворачивание групп, запоминание выбора и
// выезжающее меню на телефоне.
//
// ПРО ТЕЛЕФОН. Инженеры 73-го работают с телефонов на судах и в портах, поэтому
// на узком экране меню уезжает в бургер, а не сжимается до нечитаемого. Важная
// деталь: индикатор свежести данных при этом переезжает в шапку. В спрятанном
// меню его не видно, а решение по устаревшим данным — это несостоявшаяся
// отгрузка.
import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { useHealth, useLogout, useMe, useRightsApi } from '../api/hooks'
import { GlobalSearch } from './GlobalSearch'
import { CommandPalette } from './CommandPalette'
import { NAV_GROUPS, isCurrent, sectionOfPath, visibleItems } from './nav'
import type { NavGroup } from './nav'

/** Где помним, какие группы человек оставил открытыми. */
const OPEN_KEY = 'cir-nav-groups-open'

type OpenMap = Record<string, boolean>

function loadOpen(): OpenMap {
  try {
    const raw = localStorage.getItem(OPEN_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    if (parsed && typeof parsed === 'object') return parsed as OpenMap
  } catch {
    // Приватный режим Safari бросает на localStorage. Меню важнее памяти о нём:
    // молча работаем с состоянием по умолчанию.
  }
  return {}
}

/** Открыта ли группа: выбор пользователя, иначе значение по умолчанию из nav.ts. */
const isOpen = (g: NavGroup, saved: OpenMap): boolean => saved[g.id] ?? !g.collapsedByDefault

export default function Layout() {
  const { data: user } = useMe()
  // Права из того же /api/me: чем меню отличается у роли и что человеку про его зону
  // сказано словами. Второго запроса нет — ключ кэша один с useMe.
  const rights = useRightsApi()
  const { data: health } = useHealth()
  const logout = useLogout()
  const nav = useNavigate()
  const loc = useLocation()

  const [open, setOpen] = useState<OpenMap>(loadOpen)
  const [navOpen, setNavOpen] = useState(false)   // выехавшее меню на телефоне

  // Ручная тема: auto = как в системе, dark/light — принудительно. Хранится в
  // localStorage['cir-theme'] и ставится атрибутом data-theme на <html> — CSS в
  // styles.css знает оба пути (медиазапрос и атрибут), main.tsx применяет её до
  // первого кадра, чтобы страница не мигала системной темой при загрузке.
  const [theme, setTheme] = useState<'auto' | 'dark' | 'light'>(() => {
    try {
      const v = localStorage.getItem('cir-theme')
      return v === 'dark' || v === 'light' ? v : 'auto'
    } catch { return 'auto' }
  })
  useEffect(() => {
    const r = document.documentElement
    if (theme === 'auto') {
      delete r.dataset.theme
      try { localStorage.removeItem('cir-theme') } catch { /* приватный режим */ }
    } else {
      r.dataset.theme = theme
      try { localStorage.setItem('cir-theme', theme) } catch { /* приватный режим */ }
    }
  }, [theme])

  const stale = health && health.age_min > health.stale_after_min

  // Переход в раздел закрывает меню: иначе на телефоне поверх открытого раздела
  // остаётся панель, и человек жмёт «назад», думая, что ничего не произошло.
  useEffect(() => { setNavOpen(false) }, [loc.pathname])

  // Escape закрывает панель — привычка любого выезжающего меню.
  useEffect(() => {
    if (!navOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [navOpen])

  const toggleGroup = (id: string, next: boolean) => {
    const upd = { ...open, [id]: next }
    setOpen(upd)
    try {
      localStorage.setItem(OPEN_KEY, JSON.stringify(upd))
    } catch {
      // не смогли запомнить — не повод не сворачивать
    }
  }

  // Один и тот же индикатор в двух местах: в шапке (телефон) и под меню (монитор).
  // Лишнее прячет CSS, а не условие в коде: так они не разъедутся.
  const freshness = health ? (
    <div
      className={`freshness${stale ? ' stale' : ''}`}
      title={stale
        ? `Синхронизация с Bitrix отстала на ${Math.round(health.age_min)} мин`
          + ` (порог ${health.stale_after_min}) — данным доверять нельзя.`
        : `Последняя синхронизация с Bitrix — ${Math.round(health.age_min)} мин назад`}
    >
      <span className="dot" />
      {stale
        ? <span>синк отстал на {Math.round(health.age_min)} мин</span>
        : <span>данные свежие ({Math.round(health.age_min)} мин)</span>}
    </div>
  ) : null

  // Раздел, открытый сейчас: про него — и только про него — показываем оговорку прав
  // «этот раздел показывает заведомо неполную выборку» (`sections[].partial` в /api/me).
  const partial = rights.partial(sectionOfPath(loc.pathname))

  return (
    <div className={`app${navOpen ? ' nav-open' : ''}`}>
      {/* Командная палитра Cmd+K (дизайн 2026, «Терминал»): fixed-слой, права —
          тем же предикатом, что меню. g-последовательности включим после обкатки. */}
      <CommandPalette denied={rights.denied} />
      {/* Шапка приложения. На мониторе её нет: там меню всегда на виду. */}
      <header className="appbar">
        <button
          className="burger" type="button" aria-label="Разделы"
          aria-expanded={navOpen} onClick={() => setNavOpen((v) => !v)}
        >
          {navOpen ? '✕' : '☰'}
        </button>
        <div className="brand">CiR Core CRM</div>
        <div className="spacer" />
        {freshness}
      </header>

      {/* Затемнение под выехавшей панелью: клик мимо меню закрывает его. Намеренно
          div, а не button: панель закрывается ещё и Escape, а лишняя кнопка в
          порядке обхода клавиатурой только мешает. */}
      <div className="nav-scrim" aria-hidden="true" onClick={() => setNavOpen(false)} />

      <aside className="sidebar">
        <div className="brand">
          CiR Core CRM
          <small>ядро данных СиР</small>
        </div>

        <nav className="nav" aria-label="Разделы">
          {NAV_GROUPS.map((g) => {
            // Закрытые роли разделы (`denied_sections` из /api/me) в меню не показываем:
            // ссылка на 403 хуже отсутствующей. Это подсказка, а не защита — выборку
            // и сам доступ ограничивает BFF, что бы браузер ни нарисовал.
            const items = visibleItems(g, rights.denied)
            // Группа, все разделы которой ещё в работе, в меню не нужна.
            if (items.length === 0) return null

            // Группа с текущим разделом открыта всегда: иначе после перезагрузки
            // человек не видит, где он находится.
            const hasCurrent = items.some((i) => i.kind === 'crm' && isCurrent(loc.pathname, i.to))
            const shown = hasCurrent || isOpen(g, open)

            return (
              <section className="nav-group" key={g.id} data-open={shown}>
                <button
                  className="nav-group-head" type="button" aria-expanded={shown}
                  onClick={() => toggleGroup(g.id, !shown)}
                >
                  <span>{g.title}</span>
                  {/* У свёрнутой группы показываем, сколько там разделов: иначе
                      непонятно, стоит ли её раскрывать. */}
                  {!shown && <span className="nav-group-count">{items.length}</span>}
                  <span className="chev" aria-hidden="true">▾</span>
                </button>

                <div className="nav-group-body">
                  {items.map((i) => (
                    i.kind === 'crm' ? (
                      <NavLink key={i.to} to={i.to} className="nav-link" title={i.hint}>
                        {i.label}
                      </NavLink>
                    ) : (
                      /* Внешняя ссылка: новая вкладка, чтобы не терять место в CRM,
                         и пометка, что это старый портал, а не раздел CRM. */
                      <a
                        key={i.href} className="nav-link ext" href={i.href}
                        target="_blank" rel="noreferrer"
                        title={`${i.label} — ${i.portal}, откроется в новой вкладке.`
                          + `${i.hint ? ` ${i.hint}.` : ''}`}
                      >
                        {i.label}
                        <span className="ext-mark" aria-hidden="true">↗</span>
                      </a>
                    )
                  ))}
                  {g.note && <div className="nav-note">{g.note}</div>}
                </div>
              </section>
            )
          })}
        </nav>

        <div className="sidebar-foot">
          {/* Свежесть данных видна всегда — чтобы не принимать решений по устаревшему */}
          {freshness}
          <div>
            {user?.name}
            {/* Роль и основание зоны — словами BFF. Без них человек не может ответить
                на вопрос «почему я вижу именно это», а спросить не у кого. */}
            {rights.loaded && (
              <div className="muted" title={rights.basis ? `Зона: ${rights.basis}` : undefined}>
                {rights.roleTitle}
                {rights.roleNote ? ` — ${rights.roleNote}` : ''}
                {rights.limited && !rights.empty && ', выборка сужена вашей зоной'}
              </div>
            )}
          </div>
          {/* Тема по кругу: как в системе → тёмная → светлая. Кнопка, а не select:
              три значения переключаются одним нажатием чаще, чем выбираются из списка. */}
          <button
            type="button"
            onClick={() => setTheme(theme === 'auto' ? 'dark' : theme === 'dark' ? 'light' : 'auto')}
          >
            Тема: {theme === 'auto' ? 'как в системе' : theme === 'dark' ? 'тёмная' : 'светлая'}
          </button>
          <button
            onClick={() => logout.mutate(undefined, { onSuccess: () => nav('/login', { replace: true }) })}
            disabled={logout.isPending}
          >
            Выйти
          </button>
        </div>
      </aside>

      <main className="main">
        {/* Поиск живёт над всеми разделами: искать судно, находясь в дебиторке, — нормально. */}
        <div className="topbar">
          <GlobalSearch />
        </div>

        {/* ── Оговорки о правах: НАД любым разделом и на всех размерах экрана ──
            Почему здесь, а не в боковом меню: на телефоне меню уезжает в бургер, а самое
            дорогое из этих предупреждений — «Учётка не связана с сотрудником Bitrix». Без
            него человек видит пустую CRM и делает единственный доступный ему вывод: данных
            нет. Спрятать такую строку за бургер — то же самое, что её не показать.
            Текст берём ГОТОВЫЙ, словами BFF: он знает, чем именно сужена зона и что делать. */}
        {rights.warnings.map((w) => (
          <div className="notice" key={w}>{w}</div>
        ))}
        {/* Зона пуста — это не «ничего не найдено». Говорим об этом отдельно от warnings:
            их BFF может и не приложить, а пустая выборка на экране будет всё равно. */}
        {rights.empty && rights.warnings.length === 0 && (
          <div className="notice">
            Ваша зона видимости пуста: учётка вошла, но записей ей не видно. Пустые списки
            ниже означают «в вашей зоне ничего нет», а не «данных нет». Чинится привязкой
            учётки к сотруднику Bitrix, а не здесь.
          </div>
        )}
        {/* Раздел показывает заведомо неполную выборку сверх сужения по зоне. Молча
            недопоказанные деньги хуже отказа — поэтому текст BFF идёт как есть. */}
        {partial && (
          <div className="notice">
            Этот раздел показывает неполную выборку: {partial}
          </div>
        )}

        <Outlet />
      </main>
    </div>
  )
}
